/**
 * FaqList - the expandable question rows under a FAQ heading, shared by the
 * homepage's FaqSection and the /ehr questions.
 *
 * Native <details>/<summary> rather than React state: keyboard and screen
 * reader behaviour comes free, and nothing here needs to re-render.
 *
 * Each row is its own component so `useReveal()` is called once per instance
 * at that component's top level, never in a loop (React-Compiler safe).
 */

import type { ReactNode } from "react";
import { useReveal } from "@/components/landing/landing-animations";

/* Page ink at the same alphas the other light sections use. */
const BODY_COLOR = "hsl(225 45% 14% / 0.66)";
const RULE_COLOR = "hsl(225 45% 14% / 0.1)";

export interface FaqItem {
  q: string;
  a: ReactNode;
}

/** One expandable row. Owns its own reveal ref. */
function FaqRow({ item }: { item: FaqItem }) {
  const ref = useReveal<HTMLDetailsElement>();
  return (
    <details ref={ref} className="landing-reveal home-faq-item">
      <summary className="home-faq-summary">
        <span>{item.q}</span>
        <span aria-hidden="true" className="home-faq-marker" />
      </summary>
      <p className="home-faq-answer" style={{ color: BODY_COLOR }}>
        {item.a}
      </p>
    </details>
  );
}

export function FaqList({ items }: { items: FaqItem[] }) {
  return (
    <div style={{ borderTop: `1px solid ${RULE_COLOR}` }}>
      {items.map((item) => (
        <FaqRow key={item.q} item={item} />
      ))}

      {/* Scoped styles: strip the native disclosure marker, draw our own, and
          set the open/closed transition. Kept in CSS rather than inline so the
          `summary` pseudo-elements and the `[open]` state are reachable. */}
      <style>{`
        .home-faq-item {
          border-bottom: 1px solid ${RULE_COLOR};
        }
        .home-faq-summary {
          list-style: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 24px;
          padding: 26px 0;
          font-family: 'Space Grotesk', sans-serif;
          font-size: clamp(17px, 1.9vw, 21px);
          font-weight: 600;
          letter-spacing: -0.015em;
          line-height: 1.35;
          color: hsl(225 45% 14%);
        }
        .home-faq-summary::-webkit-details-marker { display: none; }
        .home-faq-summary::marker { content: ""; }
        .home-faq-summary:focus-visible {
          outline: 2px solid hsl(220 60% 42%);
          outline-offset: 4px;
          border-radius: 4px;
        }
        .home-faq-marker {
          position: relative;
          flex-shrink: 0;
          width: 14px;
          height: 14px;
        }
        .home-faq-marker::before,
        .home-faq-marker::after {
          content: "";
          position: absolute;
          background: hsl(225 45% 14% / 0.55);
          transition: transform 0.28s cubic-bezier(0.65, 0, 0.35, 1);
        }
        .home-faq-marker::before {
          top: 6px; left: 0; width: 14px; height: 1.5px;
        }
        .home-faq-marker::after {
          top: 0; left: 6px; width: 1.5px; height: 14px;
        }
        .home-faq-item[open] .home-faq-marker::after {
          transform: rotate(90deg);
        }
        .home-faq-answer {
          margin: 0;
          padding: 0 46px 30px 0;
          font-size: 17px;
          line-height: 1.6;
          max-width: 640px;
        }
        .home-faq-answer a {
          color: hsl(220 60% 42%);
          text-decoration: underline;
          text-underline-offset: 3px;
        }
        @media (max-width: 768px) {
          .home-faq-summary { padding: 22px 0; }
          .home-faq-answer { padding-right: 0; }
        }
      `}</style>
    </div>
  );
}
